import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import * as LucideIcons from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";

export default function PoiCard({ poi }: { poi: any }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation("common");

  function getPoiText(val: any): string {
    if (!val) return "";
    let translations = val;
    if (typeof val === "string") {
      try {
        translations = JSON.parse(val);
      } catch {
        return val;
      }
    }
    if (typeof translations === "object" && translations !== null) {
      let language = i18n.language === "en" ? "gb" : i18n.language;
      return (
        translations?.[language] ||
        translations?.["it"] ||
        Object.values(translations)[0] ||
        ""
      );
    }
    return String(val);
  }

  function toPascalCase(value: string) {
    return value
      .split(/[-_\s]+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join("");
  }

  function getCategoryIcon(iconName?: string) {
    const iconKey = toPascalCase(iconName || "Tag");
    return (LucideIcons as any)[iconKey] ?? LucideIcons.Tag;
  }

  function normalizeBase64Image(value: unknown): string | undefined {
    if (typeof value !== "string") return undefined;
    const trimmed = value.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith("data:image/")) return trimmed;
    if (/^[A-Za-z0-9+/=\r\n]+$/.test(trimmed)) {
      return `data:image/png;base64,${trimmed.replace(/\s+/g, "")}`;
    }
    return undefined;
  }

  const titleText = getPoiText(poi?.name);
  const categoryText = getPoiText(poi?.categoryName || poi?.category);
  const CategoryIcon = getCategoryIcon(poi?.categoryDefaultIcon);
  const customIconSrc = normalizeBase64Image(poi?.categoryCustomIcon);

  const rawDescription = getPoiText(poi?.description);
  const cleanDescription = rawDescription
    ? rawDescription
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;|\u00A0/g, " ")
        .trim()
    : "";

  const locationText = [poi?.city, poi?.address].filter(Boolean).join(", ");

  return (
    <Card
      className="relative mx-auto w-full max-w-sm pt-0 h-96 justify-between overflow-hidden cursor-pointer hover:shadow-lg transition-shadow duration-300"
      onClick={() => navigate(`/point-of-interest/${poi.id}`)}
    >
      {categoryText && (
        <div className="absolute top-2 left-2 z-40">
          <Badge
            variant="secondary"
            className="backdrop-blur-md bg-background/85 text-xs flex items-center gap-1 shadow-sm"
          >
            {customIconSrc ? (
              <img
                src={customIconSrc}
                alt="Category icon"
                className="h-3 w-3 mr-1 shrink-0"
              />
            ) : (
              <CategoryIcon className="h-3 w-3 mr-1 shrink-0 text-primary" />
            )}
            <span className="truncate max-w-[140px]">{categoryText}</span>
          </Badge>
        </div>
      )}

      {poi.cover ? (
        <div className="relative aspect-video w-full rounded-t-xl overflow-hidden">
          <div className="absolute inset-0 z-30 bg-black/35" />
          <img
            src={poi.cover}
            alt={titleText}
            className="relative z-20 aspect-video w-full object-cover brightness-60 dark:brightness-40"
          />
        </div>
      ) : (
        <div className="relative z-20 aspect-video w-full rounded-t-xl bg-zinc-300 dark:bg-zinc-900 flex items-center justify-center overflow-hidden">
          <LucideIcons.MapPin className="w-24 h-24 text-white dark:text-zinc-800 stroke-[1.25]" />
        </div>
      )}

      <CardHeader>
        <CardTitle className="flex gap-2 items-center truncate">
          <LucideIcons.MapPin className="w-5 h-5 shrink-0 text-primary" />
          <span className="truncate">{titleText}</span>
        </CardTitle>
        <CardDescription>
          {locationText && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <LucideIcons.MapPin className="h-3 w-3" />
              {locationText}
            </span>
          )}
        </CardDescription>
        <CardDescription>
          {cleanDescription
            ? cleanDescription.length > 85
              ? cleanDescription.substring(0, 85) + "..."
              : cleanDescription
            : locationText}
        </CardDescription>
      </CardHeader>

      <CardFooter>
        <Button type="button" className="w-full">
          {t("pointOfInterest.action")}
          <LucideIcons.ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </CardFooter>
    </Card>
  );
}
